"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const RULES = [
  {
    topic: "No subscription gate",
    rule: "Creating a tag isn't limited by your plan",
  },
  {
    topic: "Unique name",
    rule: "Tag names must be unique per company, checked case-insensitively",
  },
  {
    topic: "Color is required in the form",
    rule: "The Add Sales Order Tag screen won't let you save without picking one, though the underlying record itself doesn't strictly require it",
  },
  {
    topic: "No active/inactive status",
    rule: "There's no deactivate action on a tag, only Edit and Delete",
  },
  {
    topic: "Deleting",
    rule: "Blocked if any Sales Order still carries the tag",
  },
  {
    topic: "Who created or last changed a tag isn't shown anywhere",
    rule: "The system records it internally, but no screen in the product displays it",
  },
];

const sections = [
  {
    heading: "What this module does",
    subsections: [
      { heading: "1. A tag is a name and a color" },
      { heading: "2. Where a tag actually shows up" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p key={item.term} className="text-[16px] geist-regular text-[#374151] leading-relaxed">
        <span className="geist-medium text-[#111827]">{item.term}:</span> {item.desc}
      </p>
    ))}
  </div>
);

export default function SalesOrderTagPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        {/* Header */}
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Sales Order Tag
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Anyone labeling sales orders for quick visual tracking
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            A <span className="geist-medium text-[#111827]">Sales Order Tag</span>{" "}
            is a coloured label, Priority, Export, Repeat Customer, whatever
            your team finds useful, that you attach to a sales order to spot
            and group it at a glance.
          </p>
        </motion.div>

        <hr className="my-8" />

        {/* What this module does */}
        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this module does
          </h2>

          {/* 1. Name and color */}
          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. A tag is a name and a color
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            The Add Sales Order Tag form has exactly two things you set.
          </p>
          <Definitions
            items={[
              {
                term: "Tag Code",
                desc: "Assigned automatically with a TAG prefix.",
              },
              {
                term: "Name",
                desc: "Required, and must be unique across your company, checked without regard to letter case.",
              },
              {
                term: "Color",
                desc: "Picked from a fixed palette of named colors. The form marks it required and defaults to Gray, and what actually gets stored is that color's hex value.",
              },
            ]}
          />
          <Screenshot caption="Screenshot: the Add Sales Order Tag form, showing the auto-generated code, name, and color dropdown." />

          {/* 2. Where it shows up */}
          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Where a tag actually shows up
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            A tag exists to be attached to Sales Orders and to be visually
            recognizable once it is.
          </p>
          <Definitions
            items={[
              {
                term: "On a Sales Order",
                desc: "A tag can be assigned to a sales order so it stands out in your order list.",
              },
              {
                term: "On the tag list itself",
                desc: "Each tag renders as a coloured pill using its own stored color, not a plain text row.",
              },
              {
                term: "Created From column",
                desc: "Shows which application the tag was created in, not who created it.",
              },
            ]}
          />
          <Callout type="note">
            The system does record who created a tag and who last edited it,
            but that information isn&apos;t shown on the list, the edit
            form, or anywhere else in the product today.
          </Callout>
          <Screenshot caption="Screenshot: the Sales Order Tag list, showing a tag rendered as a coloured pill." />
        </motion.div>

        <hr className="my-8" />

        {/* Business rules */}
        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Topic
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((r, i) => (
                  <tr key={r.topic} className={i < RULES.length - 1 ? "border-b border-[#F5F5F5]" : ""}>
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[220px]">
                      {r.topic}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">{r.rule}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Related */}
        <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4">
          <Link
            href="/documentation/books/sales/sales-orders"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] transition-colors mb-1 flex items-center gap-1.5">
              Sales Orders <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#6B7280]">
              Where a tag is actually attached to an order
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
